import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard,
  Package,
  Users,
  DollarSign,
  Calculator,
  FileText,
  CheckCircle,
  UserCircle,
  Calendar,
  X,
  Shield,
  MapPin,
  ClipboardCheck,
  Navigation,
  AlertTriangle
} from 'lucide-react';

const AdminSidebar = ({ activeTab, setActiveTab, sidebarOpen, setSidebarOpen, pendingApprovalCount = 0, incidentCount = 0, adminType = 'super_admin', canSeeClientPrices = true }) => {
  const { t } = useTranslation();

  const menuSections = [
    {
      title: null,
      items: [
        { id: 'overview', label: t('overview'), icon: LayoutDashboard },
        { id: 'calendar', label: t('admin_calendar'), icon: Calendar }
      ]
    },
    {
      title: t('admin_management'),
      items: [
        { id: 'orders', label: t('orders'), icon: Package },
        { id: 'drivers', label: t('drivers'), icon: Users },
        { id: 'incidents', label: t('admin_incidents'), icon: AlertTriangle, badge: incidentCount }
      ]
    },
    {
      title: t('admin_tracking'),
      items: [
        { id: 'gps-live', label: t('admin_live_gps'), icon: Navigation },
        { id: 'inspections', label: t('admin_expert_reports'), icon: ClipboardCheck },
        { id: 'transport-services', label: t('transport_tab_title'), icon: MapPin }
      ]
    },
    // Only show Tarifas section if admin can see client prices
    ...(canSeeClientPrices ? [{
      title: t('pricing_management'),
      items: [
        { id: 'pricing', label: t('admin_general_pricing'), icon: Calculator },
        { id: 'driver-pricing', label: t('admin_driver_pricing'), icon: DollarSign },
        { id: 'custom-pricing', label: t('admin_custom_pricing'), icon: Users }
      ]
    }] : []),
    {
      title: t('admin_billing'),
      items: [
        // Only show client invoices if admin can see client prices
        ...(canSeeClientPrices ? [{ id: 'client-invoices', label: t('admin_client_invoices'), icon: FileText }] : []),
        { id: 'driver-invoices', label: t('admin_driver_invoices'), icon: FileText },
        { id: 'approvals', label: t('admin_approvals'), icon: CheckCircle, badge: pendingApprovalCount }
      ]
    },
    {
      title: t('admin_administration'),
      items: [
        // Perfiles visible for all admins (super_admin sees all, sub_admin sees only assigned)
        { id: 'profiles', label: t('admin_profiles'), icon: UserCircle },
        { id: 'documents', label: t('admin_documents'), icon: FileText },
        // Only show sub-admins option for super_admin
        ...(adminType === 'super_admin' ? [{ id: 'sub-admins', label: t('admin_collaborators'), icon: Shield }] : [])
      ]
    }
  ];

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  };

  return (
    <aside 
      className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-64 flex-shrink-0 text-white transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}
      style={{background: 'linear-gradient(to bottom, #0a3042, #083144)'}}
    >
      {/* Close button for mobile */}
      <button
        onClick={() => setSidebarOpen(false)}
        className="absolute top-4 right-4 lg:hidden text-white hover:bg-[#0d4559] p-2 rounded-lg"
        aria-label="Close sidebar"
      >
        <X className="w-5 h-5" />
      </button>

      <div className="p-6 overflow-y-auto h-full">
        {/* Logo Section */}
        <div className="flex items-center gap-3 mb-6">
          <img 
            src="https://customer-assets.emergentagent.com/job_927b78ff-6137-4209-a5c6-1343491e5188/artifacts/04fvidd7_1c.png" 
            alt="AutoGo Icon" 
            className="h-12 w-12"
          />
          <span className="text-3xl font-bold" style={{color: '#0a3042'}}>AutoGo</span>
        </div>

        {/* Navigation Menu */}
        <nav className="space-y-1">
          {menuSections.map((section, sectionIndex) => (
            <div key={sectionIndex}>
              {section.title && (
                <div className="pt-4 pb-2">
                  <p className="text-xs font-semibold text-gray-400 uppercase px-4">
                    {section.title}
                  </p>
                </div>
              )}
              {section.items.map((item) => {
                const Icon = item.icon;
                const isActive = activeTab === item.id;
                
                return (
                  <button
                    key={item.id}
                    onClick={() => handleTabClick(item.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                      isActive ? 'text-white' : 'text-gray-300 hover:bg-[#0d4559]'
                    }`}
                    style={isActive ? {backgroundColor: '#36e961'} : {}}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="text-sm">{item.label}</span>
                    {item.badge > 0 && (
                      <span className="ml-auto bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full animate-pulse">
                        {item.badge}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </nav>
      </div>
    </aside>
  );
};

export default AdminSidebar;
